import { useFamilyMemberTenant } from '@pflegenavi/frontend/api-nursing-home';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import { useUpdateTenantId } from '@pflegenavi/frontend/tenant';
import { useAuthentication } from '@pflegenavi/shared-frontend/authentication';
import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type {
  FamilyMemberResponse,
  IFMContext,
} from './types/familyMember.types';
import { useQueryClient } from '@tanstack/react-query';

export const AppContext = createContext<IFMContext | undefined>(undefined);

export const useFMContext = (): IFMContext => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('Missing AppContextProvider!');
  }
  return context;
};

export const FMContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { initializing, token } = useAuthentication(true);
  const [locale] = useState<string | null>(null);
  const [familyMember, setFamilyMember] = useState<
    FamilyMemberResponse | undefined
  >();
  const [selectedResidentId, setSelectedResidentId] = useState<
    string | undefined
  >();
  const [refetchPayments, setRefetchPayments] = useState<boolean>(false);

  const { data: tenants } = useFamilyMemberTenant();
  const { updateTenant } = useUpdateTenantId();
  const queryClient = useQueryClient();

  const doUpdateTenant = useCallback(
    (residentId: string) => {
      const tenant = tenants?.find(
        (tenant) => tenant.residentId === residentId
      );
      if (tenant) {
        updateTenant(tenant.tenant as Tenant);
      }
    },
    [tenants, updateTenant]
  );

  useEffect(() => {
    if (!token && !initializing) {
      setSelectedResidentId(undefined);
      localStorage.removeItem('selectedResidentId');
    }
  }, [token, initializing]);

  const initFamilyMember = useCallback((user: FamilyMemberResponse) => {
    setFamilyMember(user);
  }, []);

  const initResident = useCallback(
    (residentId?: string) => {
      if (residentId) {
        localStorage.setItem('selectedResidentId', residentId);
        doUpdateTenant(residentId);
      } else {
        localStorage.removeItem('selectedResidentId');
      }
      // Clear query cache when resident changes to prevent stale data
      queryClient.clear();
      setSelectedResidentId(residentId);
    },
    [doUpdateTenant, queryClient]
  );

  const doRefetchPayments = useCallback((fetch: boolean) => {
    setRefetchPayments(fetch);
  }, []);

  return (
    <AppContext.Provider
      value={{
        locale,
        familyMember,
        initFamilyMember,
        initResident,
        selectedResidentId,
        doRefetchPayments,
        refetchPayments,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
